/**
 * Store for managing the configuration state of a single merchant.
 * Handles the current merchant being viewed/edited, their tax configuration,
 * and related loading and update state for the merchant configuration UI.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { CommonMerchantGetMerchantData } from '@/types/commonMerchantGetMerchantData'
import type { CommonMerchantShopifyStores } from '@/types/commonMerchantShopifyStores'
import type { TaxConfig } from '@/types/taxConfig'
import { useMerchantStore } from './merchantStore'
import { AuthState } from '@/types/authState'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

export interface MerchantConfigurationState {
  currentMerchant: CommonMerchantGetMerchantData | null
  taxConfig: TaxConfig | null
  isLoading: boolean
  lastUpdated: Date | null
  error: ApiError | null
}

export const useMerchantConfigurationStore = defineStore(
  'merchantConfiguration',
  () => {
    // State
    const currentMerchant = ref<CommonMerchantGetMerchantData | null>(null)
    const taxConfig = ref<TaxConfig | null>(null)

    const isLoading = ref(false)
    const lastUpdated = ref<Date | null>(null)
    const error = ref<ApiError | null>(null)

    // Getters
    const hasTaxConfig = computed(() => taxConfig.value !== null)

    const totalFilingCountries = computed(
      () => taxConfig.value?.FilingCountries?.length || 0,
    )

    const providerType = computed(() => {
      return taxConfig.value?.ProviderConfig?.Type || null
    })

    // Actions
    const setCurrentMerchant = (
      merchant: CommonMerchantGetMerchantData | null,
    ) => {
      currentMerchant.value = merchant
    }

    const updateStoreData = (
      merchantId: string,
      storeId: string,
      updatedStoreData: CommonMerchantShopifyStores,
    ) => {
      if (!currentMerchant.value || currentMerchant.value.Id !== merchantId) {
        return
      }

      // Find and update the specific store in the merchant's ShopifyStores array
      const storeIndex = currentMerchant.value.ShopifyStores?.findIndex(
        store => store.Id === storeId,
      )

      if (
        storeIndex !== undefined &&
        storeIndex >= 0 &&
        currentMerchant.value.ShopifyStores
      ) {
        currentMerchant.value.ShopifyStores[storeIndex] = updatedStoreData
        lastUpdated.value = new Date()
      }
    }

    const setTaxConfig = (config: TaxConfig | null) => {
      taxConfig.value = config
      lastUpdated.value = new Date()
    }

    const updateTaxConfig = (updatedConfig: Partial<TaxConfig>) => {
      if (taxConfig.value) {
        taxConfig.value = {
          ...taxConfig.value,
          ...updatedConfig,
        }
        lastUpdated.value = new Date()
      }
    }

    const removeTaxConfig = () => {
      taxConfig.value = null
      lastUpdated.value = new Date()
    }

    const setLoading = (loading: boolean) => {
      isLoading.value = loading
    }

    const resetState = () => {
      currentMerchant.value = null
      taxConfig.value = null
      isLoading.value = false
      lastUpdated.value = null
      error.value = null
    }

    const clearError = () => {
      error.value = null
    }

    const loadTaxConfigForMerchant = async (
      authState: AuthState,
      merchantId: string,
    ) => {
      error.value = null
      try {
        const { taxConfigApi } = await import('@/services/taxConfigService')
        const config = await taxConfigApi.getTaxConfigById(
          authState,
          merchantId,
        )
        setTaxConfig(config)
        return config
      } catch (err) {
        error.value = normalizeError(err)
        console.warn('Could not load tax config:', error.value.message)
        setTaxConfig(null)
        return null
      }
    }

    // Refresh merchant list and tax config
    const refreshAll = async (authState: AuthState) => {
      if (!currentMerchant.value?.Id) return

      error.value = null
      try {
        isLoading.value = true

        const merchantStore = useMerchantStore()
        // Store the current query params to restore the full merchant list later
        const originalParams = merchantStore.getCurrentParams()

        // Invalidate cache so we get fresh data from the API
        merchantStore.invalidateCache()

        // Fetch the updated merchant with ID filter
        await merchantStore.fetchMerchants(authState, {
          Id: currentMerchant.value.Id,
        })

        const updatedMerchant = merchantStore.merchants.find(
          m => m.Id === currentMerchant.value?.Id,
        )

        if (updatedMerchant) {
          setCurrentMerchant(updatedMerchant)
        }

        // Restore the full merchant list by re-fetching with original params
        // This ensures the UnifiedMerchantManagementView table shows all merchants
        await merchantStore.fetchMerchants(authState, originalParams)

        await loadTaxConfigForMerchant(authState, currentMerchant.value!.Id)

        lastUpdated.value = new Date()
      } catch (err) {
        error.value = normalizeError(err)
        console.error('Error refreshing all merchant data:', error.value.message)
        throw error.value
      } finally {
        isLoading.value = false
      }
    }

    const updateBusinessEntityField = (
      storeId: string,
      entityId: string,
      field: 'IsReachTax',
      value: boolean,
    ) => {
      const store = currentMerchant.value?.ShopifyStores?.find(
        s => s.Id === storeId,
      )
      const entity = store?.BusinessEntities?.find(e => e.Id === entityId)
      if (entity) {
        entity[field] = value
        lastUpdated.value = new Date()
      }
    }

    const updateBankAccountField = (
      storeId: string,
      entityId: string,
      bankAccountId: string,
      field: 'IsReach',
      value: boolean,
    ) => {
      const store = currentMerchant.value?.ShopifyStores?.find(
        s => s.Id === storeId,
      )
      const entity = store?.BusinessEntities?.find(e => e.Id === entityId)
      const account = entity?.BankAccounts?.find(a => a.Id === bankAccountId)
      if (account) {
        account[field] = value
        lastUpdated.value = new Date()
      }
    }

    return {
      // State
      currentMerchant,
      taxConfig,
      isLoading,
      lastUpdated,
      error,

      // Getters
      hasTaxConfig,
      totalFilingCountries,
      providerType,

      // Actions
      setCurrentMerchant,
      setTaxConfig,
      updateTaxConfig,
      removeTaxConfig,
      setLoading,
      resetState,
      clearError,
      loadTaxConfigForMerchant,
      refreshAll,
      updateStoreData,
      updateBusinessEntityField,
      updateBankAccountField,
    }
  },
)
